import React from "react";
import { Box, Stack, Group, Avatar } from "@mantine/core";
import {
  IconBrandLinkedin,
  IconBrandTwitter,
  IconMail,
  IconWorld,
  IconStar,
} from "@tabler/icons-react";
import { Card } from "./UI/Card";
import { DimmedText, MainText } from "./UI/Typography";
import VideoEmbed from "./VideoEmbed";

interface Testimonial {
  id: number;
  name: string;
  text: string;
  photo?: string;
  title?: string;
  rating?: number;
  videoUrl?: string;
  source?: string;
  date?: string;
  featured: boolean;
  tags: string[];
}

interface TestimonialCardProps {
  testimonial: Testimonial;
}

const getSourceIcon = (source?: string) => {
  switch (source?.toLowerCase()) {
    case "linkedin":
      return <IconBrandLinkedin size={16} />;
    case "twitter":
      return <IconBrandTwitter size={16} />;
    case "email":
      return <IconMail size={16} />;
    case "website":
      return <IconWorld size={16} />;
    default:
      return null;
  }
};

const formatDate = (dateString?: string) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", { year: "numeric", month: "short" });
};

export const TestimonialCard: React.FC<TestimonialCardProps> = ({ testimonial }) => {
  const [hovered, setHovered] = React.useState(false);
  const isVideo = !!testimonial.videoUrl;

  return (
    <Card
      shadow="md"
      padding="0"
      radius="md"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        overflow: "hidden",
        transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        boxShadow: hovered
          ? "0 24px 64px rgba(212,85,43,0.2), 0 0 0 1px rgba(212,85,43,0.28), inset 0 1px 0 rgba(255,255,255,0.1)"
          : "inset 0 1px 0 rgba(255,255,255,0.07), 0 8px 32px rgba(0,0,0,0.4)",
        border: hovered
          ? "1px solid rgba(212,85,43,0.28)"
          : "1px solid rgba(212,85,43,0.12)",
        height: "fit-content",
        display: "flex",
        flexDirection: "column",
        width: isVideo ? "100%" : undefined,
      }}
    >
      {isVideo ? (
        <>
          <Box style={{ position: "relative", width: "100%" }}>
            <VideoEmbed url={testimonial.videoUrl!} title={`${testimonial.name} - Testimonial`} />
          </Box>
          <Stack gap="sm" p="md">
            <Group gap="xs" align="center">
              {testimonial.photo && (
                <Avatar src={testimonial.photo} size="sm" radius="xl" />
              )}
              <Stack gap={0} style={{ flex: 1 }}>
                <MainText size="sm" style={{ fontWeight: 600 }}>
                  {testimonial.name}
                </MainText>
                {testimonial.title && (
                  <DimmedText size="xs">{testimonial.title}</DimmedText>
                )}
              </Stack>
              {testimonial.source && (
                <Box style={{ color: "rgba(212,85,43,0.7)" }}>
                  {getSourceIcon(testimonial.source)}
                </Box>
              )}
            </Group>
            {testimonial.rating && (
              <Group gap={4}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <IconStar
                    key={i}
                    size={14}
                    fill={i < testimonial.rating! ? "#d4552b" : "transparent"}
                    color={i < testimonial.rating! ? "#d4552b" : "rgba(245,238,224,0.28)"}
                    style={{ strokeWidth: 1.5 }}
                  />
                ))}
              </Group>
            )}
          </Stack>
        </>
      ) : (
        <Stack gap="md" p="lg" style={{ flex: 1 }}>
          {/* Opening quote accent */}
          <Box
            style={{
              fontSize: "3rem",
              lineHeight: 1,
              color: "#d4552b",
              opacity: 0.5,
              fontFamily: "Georgia, serif",
              marginBottom: "-0.5rem",
            }}
          >
            "
          </Box>

          <MainText
            size="sm"
            style={{
              lineHeight: 1.75,
              flex: 1,
              fontStyle: "italic",
            }}
          >
            {testimonial.text}
          </MainText>

          {testimonial.rating && (
            <Group gap={4}>
              {Array.from({ length: 5 }).map((_, i) => (
                <IconStar
                  key={i}
                  size={16}
                  fill={i < testimonial.rating! ? "#d4552b" : "transparent"}
                  color={i < testimonial.rating! ? "#d4552b" : "rgba(245,238,224,0.28)"}
                  style={{ strokeWidth: 1.5 }}
                />
              ))}
            </Group>
          )}

          <Group
            gap="sm"
            align="center"
            style={{
              marginTop: "auto",
              paddingTop: "0.75rem",
              borderTop: "1px solid rgba(212,85,43,0.1)",
            }}
          >
            {testimonial.photo && (
              <Avatar src={testimonial.photo} size="md" radius="xl" />
            )}
            <Stack gap={2} style={{ flex: 1 }}>
              <MainText size="sm" style={{ fontWeight: 700 }}>
                {testimonial.name}
              </MainText>
              {testimonial.title && (
                <DimmedText size="xs">{testimonial.title}</DimmedText>
              )}
            </Stack>
            {testimonial.source && (
              <Box style={{ color: "rgba(212,85,43,0.7)" }}>
                {getSourceIcon(testimonial.source)}
              </Box>
            )}
          </Group>

          {testimonial.date && (
            <DimmedText size="xs">{formatDate(testimonial.date)}</DimmedText>
          )}
        </Stack>
      )}
    </Card>
  );
};

export default TestimonialCard;
